import type { IteroClinic } from '../../../../services/iteroConnection';

const COMBINING_MARKS = /\p{M}/gu;
const WHITESPACE = /\s+/;

export const CLINIC_SEARCH_PLACEHOLDER = 'Search by clinic name, iTero ID or linked practice';

export function foldForSearch(text: string): string {
    return text.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
}

function searchTokens(query: string): string[] {
    return foldForSearch(query.trim()).split(WHITESPACE).filter(Boolean);
}

function searchableFields(clinic: IteroClinic): string[] {
    return [
        foldForSearch(clinic.name),
        String(clinic.clinicId),
        ...clinic.linkedPractices.map((practice) => foldForSearch(practice.name))
    ];
}

// Every word must appear in some field, so "smile 6863" narrows by name and ID together.
export function clinicMatchesQuery(clinic: IteroClinic, query: string): boolean {
    const tokens = searchTokens(query);
    if (tokens.length === 0) return true;
    const fields = searchableFields(clinic);
    return tokens.every((token) => fields.some((field) => field.includes(token)));
}

function compareClinics(a: IteroClinic, b: IteroClinic): number {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return byName !== 0 ? byName : a.clinicId - b.clinicId;
}

export function sortClinics(clinics: IteroClinic[]): IteroClinic[] {
    return [...clinics].sort(compareClinics);
}

export function filterClinics(clinics: IteroClinic[], query: string): IteroClinic[] {
    return clinics.filter((clinic) => clinicMatchesQuery(clinic, query));
}

export function clinicCountText(shown: number, total: number): string {
    return `${shown} of ${total} ${total === 1 ? 'clinic' : 'clinics'}`;
}

export function noMatchText(query: string): string {
    return `No clinics match "${query.trim()}".`;
}

export function selectedClinicText(clinic: IteroClinic | undefined): string | null {
    if (!clinic || clinic.isCurrent) return null;
    return `Selected: ${clinic.name}`;
}
